import React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Typography, Paper, Chip, Breadcrumbs, Link } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

export default function SeriesTable({ coins }) {
    const { denom, series } = useParams();
    const navigate = useNavigate();

    // Decode params
    const decodedDenom = decodeURIComponent(denom);
    const decodedSeries = decodeURIComponent(series);

    // Filter Coins
    const filteredCoins = coins.filter(c => {
        const s_name = c.series_name || 'Other';
        const s_date = c.series_date || '';
        const s = s_date ? `${s_date} ${s_name}` : s_name;
        return s === decodedSeries;
    });

    // Define Columns explicitly
    const columns = [
        { field: 'pcgs_id', headerName: 'PCGS #', width: 90 },
        { field: 'date', headerName: 'Date', width: 90, sortable: true },
        { field: 'mint', headerName: 'Mint', width: 70 },
        { field: 'major', headerName: 'Major Variety', width: 200 },
        { field: 'type', headerName: 'Type', width: 180 },
        {
            field: 'variety',
            headerName: 'Variety / Description',
            flex: 1,
            minWidth: 200,
            valueGetter: (value, row) => value || row.major
        },
        { field: 'country', headerName: 'Country', width: 100 },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const statusVal = params.value || '';
                const isOwned = statusVal.toLowerCase().includes('bought') || statusVal.toLowerCase().includes('have');
                return (
                    <Chip
                        label={isOwned ? 'OWNED' : 'WANTED'}
                        color={isOwned ? 'success' : 'error'}
                        size="small"
                        variant={isOwned ? 'filled' : 'outlined'}
                    />
                );
            }
        },
        {
            field: 'grade',
            headerName: 'Grade',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value, row) => {
                return value || (row.specimens && row.specimens.length > 0 ? row.specimens[0].grade : '-');
            }
        },
    ];

    return (
        <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                    Home
                </Link>
                <Typography color="text.primary">{decodedDenom}</Typography>
                <Typography color="text.primary">{decodedSeries}</Typography>
            </Breadcrumbs>

            <Paper sx={{ height: 'calc(100vh - 180px)', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 3, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                                {decodedSeries}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click a coin row to view details & specimens
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Paper sx={{ p: 1.5, bgcolor: 'primary.light', color: 'primary.contrastText', minWidth: 150 }}>
                                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: '0.8rem', color: 'inherit' }}>Total Cost</Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: 'inherit' }}>
                                    ${filteredCoins.reduce((sum, coin) => sum + (coin.specimens || []).reduce((s, spec) => s + (Number(spec.purchase_price) || 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </Paper>
                            <Paper sx={{ p: 1.5, bgcolor: 'secondary.main', color: 'secondary.contrastText', minWidth: 150 }}>
                                <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: '0.8rem', color: 'inherit' }}>Total Value</Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: 'inherit' }}>
                                    ${filteredCoins.reduce((sum, coin) => sum + (coin.specimens || []).reduce((s, spec) => s + (Number(spec.pcgs_value) || 0), 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                            </Paper>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <DataGrid
                        rows={filteredCoins}
                        columns={columns}
                        getRowId={(row) => row.pcgs_id}
                        onRowClick={(params) => navigate(`/coin/${params.row.pcgs_id}`)}
                        pageSize={25}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableSelectionOnClick={false}
                        components={{ Toolbar: GridToolbar }}
                        density="compact"
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                        sx={{
                            border: 0,
                            height: '100%',
                            '& .MuiDataGrid-cell:focus': {
                                outline: 'none',
                            },
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
